import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InboxEntryPriority } from '@this+that/models';

import type { GenerateDoBoxEntry } from '../../../../services/userCommunicationAgents.service';
import type { DoBoxFormInitialValues } from '../components';

type UseDoBoxAiInitialValuesReturn = {
  aiInitialValues?: DoBoxFormInitialValues;
  aiNoticeText: string | null;
  aiFormKey: number;
  clearAiInitialValues: () => void;
  handleAiDoBoxResult: (result: unknown) => boolean;
  handleAiEntryCreated: () => void;
  handleAiFormClose: (close: () => void) => void;
};

function toInitialValues(
  entry: GenerateDoBoxEntry,
  activeTaskGroupId?: string | null,
): DoBoxFormInitialValues {
  return {
    subject: entry.subject,
    description: entry.description ?? undefined,
    dueAt: entry.dueAt ?? undefined,
    priority:
      entry.priority != null
        ? (entry.priority as InboxEntryPriority)
        : undefined,
    taskGroupId: entry.taskGroupId ?? activeTaskGroupId ?? undefined,
    taskLabelIds: entry.taskLabelIds ?? [],
    participantIds: entry.participantIds ?? [],
  };
}

export function useDoBoxAiInitialValues(
  activeTaskGroupId?: string | null,
): UseDoBoxAiInitialValuesReturn {
  const { t } = useTranslation();
  const [queue, setQueue] = useState<DoBoxFormInitialValues[]>([]);
  const [total, setTotal] = useState(0);
  const [aiFormKey, setAiFormKey] = useState(0);
  const justAdvancedRef = useRef(false);

  const clearAiInitialValues = useCallback(() => {
    setQueue([]);
    setTotal(0);
  }, []);

  const handleAiDoBoxResult = useCallback(
    (result: unknown) => {
      const entries = (result as { entries?: GenerateDoBoxEntry[] }).entries;
      if (!entries?.length) {
        return false;
      }
      setQueue(
        entries.map((entry) => toInitialValues(entry, activeTaskGroupId)),
      );
      setTotal(entries.length);
      setAiFormKey((key) => key + 1);
      return true;
    },
    [activeTaskGroupId],
  );

  const handleAiEntryCreated = useCallback(() => {
    if (queue.length <= 1) {
      return;
    }
    justAdvancedRef.current = true;
    setQueue(queue.slice(1));
    setAiFormKey((key) => key + 1);
  }, [queue]);

  const handleAiFormClose = useCallback(
    (close: () => void) => {
      if (justAdvancedRef.current) {
        justAdvancedRef.current = false;
        return;
      }
      close();
      clearAiInitialValues();
    },
    [clearAiInitialValues],
  );

  let aiNoticeText: string | null = null;
  if (total === 1) {
    aiNoticeText = t('doBox.aiAssistant.singleTaskNotice');
  } else if (total > 1) {
    aiNoticeText = t('doBox.aiAssistant.batchNotice', {
      current: total - queue.length + 1,
      total,
    });
  }

  return {
    aiInitialValues: queue[0],
    aiNoticeText,
    aiFormKey,
    clearAiInitialValues,
    handleAiDoBoxResult,
    handleAiEntryCreated,
    handleAiFormClose,
  };
}
